import { useEffect, useRef, useState } from "react";
import api, { apiError, brl, printAgent } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, LockOpen, Lock, History, ArrowLeftRight, ArrowUpCircle, ArrowDownCircle, Scale, Pencil, Trash2, CalendarDays, Printer } from "lucide-react";
import { ConfirmDialog } from "@/components/panel/ui";
import usePaginatedList from "@/hooks/usePaginatedList";
import Pagination from "@/components/panel/Pagination";
import { Badge, Button, Card, DataTable, Field, Input, PageHeader } from "@/components/ui-kit";
import MovementDialog from "./cash/MovementDialog";
import { buildCashMovementHtml } from "@/lib/thermalDoc";
import AdjustDialog from "./cash/AdjustDialog";
import CloseCashDialog from "./cash/CloseCashDialog";

const METHOD_LABEL = { dinheiro: "Dinheiro", pix: "PIX", cartao_debito: "Cartão débito", cartao_credito: "Cartão crédito", misto: "Misto" };

const hora = (iso) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

/**
 * A diferença de fechamento é status, não só número: falta (negativo) é o caso
 * que o financeiro cobra do operador, sobra pede conferência e zero é o turno
 * que bateu. Por isso vira Badge com tom semântico, não texto colorido.
 */
const diffTone = (d) => (d < 0 ? "danger" : d > 0 ? "warning" : "success");

/** Ordem fixa: a leitura é sempre a mesma, mesmo quando uma forma não teve venda. */
const FORMAS = ["dinheiro", "pix", "cartao_debito", "cartao_credito"];

/**
 * O que entrou no turno, por forma — com a proporção à vista.
 *
 * A barra não é enfeite: no fechamento o que importa é quanto do dia foi
 * DINHEIRO (vai para a gaveta e precisa ser contado) contra quanto foi cartão e
 * PIX (não passa pela gaveta, confere-se no extrato). Duas linhas de texto não
 * respondiam isso de relance; a proporção responde.
 *
 * As formas sem movimento continuam na lista, apagadas: "o PIX deu zero" é uma
 * conferência, e esconder a linha faz parecer que ninguém olhou.
 */
function ByMethod({ byMethod = {} }) {
  const total = FORMAS.reduce((s, m) => s + (byMethod[m] || 0), 0);

  return (
    /* `space-y-4` e não `2.5`: com pouco respiro a barra de uma linha encostava
       no rótulo da linha seguinte e parecia pertencer a ela. A barra tem que
       ficar visivelmente mais perto do próprio número do que do vizinho. */
    <div className="space-y-4">
      {FORMAS.map((m) => {
        const v = byMethod[m] || 0;
        const pct = total > 0 ? (v / total) * 100 : 0;

        return (
          <div key={m} data-testid="cash-by-method">
            <div className="flex items-baseline justify-between gap-3">
              <span className={`text-sm ${v > 0 ? "text-slate-700" : "text-slate-400"}`}>{METHOD_LABEL[m] || m}</span>
              <span className={`font-mono tabular-nums text-sm ${v > 0 ? "font-medium text-slate-900" : "text-slate-300"}`}>
                {v > 0 ? brl(v) : "—"}
              </span>
            </div>
            {/* Trilho sempre visível: a barra vazia é informação (esta forma não
                teve movimento), não um espaço que faltou preencher. */}
            <div className="mt-1.5 h-1 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full ${m === "dinheiro" ? "bg-brand-600" : "bg-slate-300"}`}
                style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Uma linha da derivação do esperado. O sinal vem junto do número, não do rótulo. */
function LinhaConta({ rotulo, valor, sinal, tom = "text-slate-500" }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className={tom}>{rotulo}</span>
      <span className={`font-mono tabular-nums ${tom}`}>
        {sinal && <span className="text-slate-300 mr-0.5">{sinal}</span>}{brl(Math.abs(valor))}
      </span>
    </div>
  );
}

/**
 * Conferência do DIA por forma de pagamento — todas as sessões juntas.
 *
 * O card de cima ("Recebido por forma de pagamento") fala do TURNO EM ABERTO, e
 * só existe enquanto há caixa aberto. Isso deixava três perguntas sem resposta:
 * o dia em que a gaveta abriu duas vezes (troca de operador) tinha o PIX partido
 * entre dois turnos; depois do fechamento o resumo sumia da tela junto com o
 * turno; e a venda registrada sem sessão aberta não entrava em resumo nenhum.
 *
 * Este bloco responde a pergunta do fim do expediente — "quanto entrou hoje, e
 * em quê?" — e continua respondendo com o caixa fechado. O seletor de data é o
 * que permite conferir ontem sem sair da tela.
 */
function DaySummary({ data, date, onDateChange, loading }) {
  const metodos = data?.by_method || [];
  const totalMetodos = metodos.reduce((s, m) => s + m.total, 0);

  return (
    <section className="space-y-3" data-testid="cash-day-summary">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-sm font-semibold text-slate-900 flex items-center gap-2">
          <CalendarDays size={16} className="text-slate-400" aria-hidden /> Conferência do dia
        </h2>
        <Field label="Dia" labelTone="plain" className="flex items-center gap-1.5">
          <Input type="date" value={date} onChange={(e) => onDateChange(e.target.value)}
            data-testid="cash-day-date" className="w-auto px-2.5" />
        </Field>
      </div>

      <Card className="p-5">
        {loading ? (
          <p className="text-sm text-slate-400">Somando o dia…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pb-4 border-b border-slate-100">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Vendas</p>
                <p className="font-mono text-lg font-semibold text-slate-900" data-testid="cash-day-count">
                  {data?.sales_count ?? 0}
                </p>
                {!!data?.cancelled_count && (
                  <p className="text-xs text-red-500">{data.cancelled_count} cancelada(s)</p>
                )}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Total recebido</p>
                <p className="font-mono text-lg font-semibold text-emerald-700" data-testid="cash-day-total">
                  {brl(data?.sales_total ?? 0)}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Sangrias</p>
                <p className="font-mono text-lg font-semibold text-red-600">− {brl(data?.sangria ?? 0)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Suprimentos</p>
                <p className="font-mono text-lg font-semibold text-emerald-700">+ {brl(data?.suprimento ?? 0)}</p>
              </div>
            </div>

            <p className="text-sm font-semibold text-slate-900 mt-4 mb-2">Por forma de pagamento</p>
            {metodos.length === 0 ? (
              <p className="text-sm text-slate-400">Nenhum pagamento neste dia.</p>
            ) : (
              <div className="space-y-1.5">
                {metodos.map((m) => (
                  <div key={m.method} className="flex items-center justify-between gap-3 text-sm"
                    data-testid="cash-day-method">
                    <span className="text-slate-600">
                      {METHOD_LABEL[m.method] || m.method}
                      <span className="text-slate-400 font-mono text-xs"> · {m.count}x</span>
                    </span>
                    {/* A barra dá a proporção de relance: é o que responde "o
                        cartão está comendo o dia inteiro?" sem fazer conta. */}
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="hidden sm:block w-24 h-1.5 rounded-full bg-slate-100 overflow-hidden" aria-hidden>
                        <span className="block h-full bg-brand-500"
                          style={{ width: `${totalMetodos > 0 ? (m.total / totalMetodos) * 100 : 0}%` }} />
                      </span>
                      <span className="font-mono font-medium tabular-nums">{brl(m.total)}</span>
                    </span>
                  </div>
                ))}
                <div className="flex justify-between text-sm pt-2 mt-1 border-t border-slate-100 font-semibold">
                  <span className="text-slate-700">Total</span>
                  <span className="font-mono tabular-nums text-slate-900">{brl(totalMetodos)}</span>
                </div>
              </div>
            )}

            {/* Duas ou mais sessões no mesmo dia explicam dois fundos de troco
                diferentes no histórico — dizer isso aqui evita a leitura de que
                o caixa "bateu errado". */}
            {(data?.sessions?.length ?? 0) > 1 && (
              <p className="text-xs text-slate-400 mt-3">
                A gaveta abriu {data.sessions.length} vezes neste dia — os valores acima somam todas.
              </p>
            )}
          </>
        )}
      </Card>
    </section>
  );
}

export default function CashRegister() {
  const [current, setCurrent] = useState(null); // { session, summary }
  const [openAmount, setOpenAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [movementType, setMovementType] = useState(null); // 'sangria' | 'suprimento'
  const [editingMovement, setEditingMovement] = useState(null);
  const [removingMovement, setRemovingMovement] = useState(null);
  const [adjusting, setAdjusting] = useState(false);
  const [closing, setClosing] = useState(false);

  // O vazio do histórico precisa oferecer saída, e a saída daqui é abrir o
  // caixa (campo logo acima) ou fechar o turno, que agora abre um modal.
  const openAmountRef = useRef(null);

  // Conferência do dia: independente do turno em aberto, para o resumo não
  // sumir quando o caixa fecha nem se partir quando a gaveta abre duas vezes.
  const [day, setDay] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [daySummary, setDaySummary] = useState(null);
  const [dayLoading, setDayLoading] = useState(true);

  // O resumo de cada sessão agora vem calculado em lote pelo servidor.
  const history = usePaginatedList("/pos/cash-session/history", {}, { perPage: 20 });

  const loadCurrent = () => api.get("/pos/cash-session/current").then((r) => setCurrent(r.data)).catch(() => {});
  const loadDay = (data = day) => {
    setDayLoading(true);
    return api.get("/pos/cash-session/day-summary", { params: { date: data } })
      .then((r) => setDaySummary(r.data))
      .catch(() => setDaySummary(null))
      .finally(() => setDayLoading(false));
  };
  const load = () => { loadCurrent(); loadDay(); history.refetch(); };
  useEffect(() => { loadCurrent(); }, []);
  // `day` na dependência: trocar a data refaz só esta consulta.
  useEffect(() => {
    loadDay(day);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  const openCaixa = async () => {
    setBusy(true);
    try {
      await api.post("/pos/cash-session/open", { opening_amount: parseFloat(openAmount) || 0 });
      toast.success("Caixa aberto");
      setOpenAmount(""); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  // Comprovante da sangria/suprimento em bobina. Sai da LINHA do extrato, então
  // reimprimir um lançamento de mais cedo no turno continua possível.
  const imprimirMovimento = async (m) => {
    const tid = toast.loading("Enviando comprovante para a impressora...");
    try {
      await printAgent.printReceipt(
        buildCashMovementHtml(m, { operador: session?.opened_by_name, sessao: session?.number }),
        { label: `Caixa ${m.label}` },
      );
      toast.success("Comprovante enviado", { id: tid });
    } catch (e) {
      toast.error(e.message || "Falha ao imprimir", { id: tid });
    }
  };

  const removeMovement = async (m) => {
    try {
      await api.delete(`/pos/cash-session/movements/${m.id}`);
      toast.success("Lançamento removido");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRemovingMovement(null); }
  };

  const session = current?.session;
  const summary = current?.summary;
  const movements = current?.movements || [];
  // Mesma conta do backend (cashExpected). O ajuste soma porque já carrega o
  // próprio sinal: sobrou é positivo, faltou é negativo.
  const expectedCash = (session?.opening_amount || 0)
    + (summary?.by_method?.dinheiro || 0)
    + (summary?.suprimento || 0)
    - (summary?.sangria || 0)
    + (summary?.ajuste || 0);

  return (
    <div className="space-y-6" data-testid="cash-register-page">
      {/* A tela é a mesma para admin, vendedor e balcão; "Balcão" é a seção onde
          ela vive no menu do admin e descreve onde a gaveta fica de verdade. */}
      <PageHeader
        eyebrow="Balcão"
        title="Frente de Caixa"
        icon={Wallet}
        description="Abertura, sangria, suprimento e fechamento do turno da gaveta. A diferença entre o esperado e o contado fica registrada no histórico, no nome de quem fechou."
      />

      {!session ? (
        <Card className="p-6 max-w-md" data-testid="cash-open-card">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-1"><LockOpen size={18} className="text-emerald-600" aria-hidden /> Caixa fechado</h2>
          <p className="text-sm text-slate-500 mb-4">Abra o caixa informando o fundo de troco inicial.</p>
          <Field label="Fundo de troco (R$)" className="mb-4">
            <Input ref={openAmountRef} type="number" step="0.01" value={openAmount} onChange={(e) => setOpenAmount(e.target.value)} placeholder="0,00"
              data-testid="cash-open-amount" mono />
          </Field>
          <Button onClick={openCaixa} loading={busy} size="lg" block data-testid="cash-open-btn">Abrir caixa</Button>
        </Card>
      ) : (
        /* Uma faixa de apoio no topo, o extrato ocupando a largura toda embaixo.
           Antes o extrato dividia a linha com o card de fechamento e sobrava com
           2/3 da tela: as sete colunas não cabiam, o `overflow-x-auto` do
           DataTable virava uma barra de rolagem horizontal DENTRO da página que
           já rolava na vertical, e em monitor menor a tela tinha dois scrolls
           concorrentes. Dando a largura inteira ao extrato, o scroll interno
           deixa de existir — os dois problemas eram o mesmo. */
        <div className="space-y-4">
          {/* Dois painéis no lugar de cinco cards.
              A tela tinha cinco superfícies de peso visual igual, e a prioridade
              saía invertida: "Vendas hoje: 3" era o maior número da página
              enquanto "Esperado em dinheiro" — o único que se usa para agir —
              era o menor texto cinza. Um card inteiro exibia R$ 0,00 de fundo de
              troco. Aqui a gaveta domina e o resto vira referência. */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
            {/* ---------- A gaveta ----------
                O esperado aparece como uma CONTA que fecha à vista, no formato
                de quem confere dinheiro: as parcelas com o próprio sinal, o
                traço, e o total. O fundo de troco deixou de ser um card avulso e
                voltou para a linha onde ele significa alguma coisa — a primeira
                parcela da soma. */}
            <Card className="p-5 lg:col-span-7 flex flex-col" data-testid="cash-close-card">
              {/* Rótulo e selo dividem a primeira linha — os dois são pequenos —
                  e o número fica sozinho embaixo, na largura toda. Com o número
                  ao lado do selo, no celular o selo era empurrado para fora da
                  borda do cartão. */}
              <div className="flex items-center justify-between gap-2">
                <p className="text-eyebrow uppercase text-slate-500">Esperado na gaveta</p>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-xs font-medium text-emerald-700 shrink-0">
                  <LockOpen size={13} aria-hidden /> Turno aberto
                </span>
              </div>
              <p className="mt-2 font-mono tabular-nums text-display font-semibold text-slate-900 break-all"
                data-testid="cash-expected">{brl(expectedCash)}</p>

              <div className="mt-4 pt-4 border-t border-slate-100 space-y-1.5">
                <LinhaConta rotulo="Fundo de troco" valor={session.opening_amount || 0} />
                <LinhaConta rotulo="Vendas em dinheiro" valor={summary?.by_method?.dinheiro || 0} sinal="+" />
                {(summary?.suprimento || 0) > 0 && (
                  <LinhaConta rotulo="Suprimentos" valor={summary.suprimento} sinal="+" tom="text-emerald-600" />
                )}
                {(summary?.sangria || 0) > 0 && (
                  <LinhaConta rotulo="Sangrias" valor={summary.sangria} sinal="−" tom="text-red-500" />
                )}
                {!!(summary?.ajuste) && (
                  <LinhaConta rotulo="Ajustes" valor={summary.ajuste}
                    sinal={summary.ajuste < 0 ? "−" : "+"} tom="text-amber-600" />
                )}
              </div>

              {/* `mt-auto` empurra o rodapé para baixo, e a conta acima pode
                  crescer com sangria e ajuste sem deslocá-lo. O espaço que sobra
                  é preenchido por informação de verdade — quem abriu e quando —
                  em vez de ar: essa linha vivia solta no pé da página, longe do
                  turno a que se refere. */}
              <div className="mt-auto pt-5">
                <Button onClick={() => setClosing(true)} size="lg" block icon={Lock}
                  data-testid="cash-close-btn">
                  Conferir e fechar caixa
                </Button>
                <p className="mt-2.5 text-xs text-slate-400 text-center">
                  Aberto por {session.opened_by_name} · {new Date(session.opened_at).toLocaleString("pt-BR", {
                    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                  })}
                </p>
              </div>
            </Card>

            {/* ---------- O turno ---------- */}
            <Card className="p-5 lg:col-span-5 flex flex-col">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-eyebrow uppercase text-slate-500">Recebido no turno</p>
                <p className="text-xs text-slate-400" data-testid="cash-kpi-count">
                  {summary?.sales_count ?? 0} {(summary?.sales_count ?? 0) === 1 ? "venda" : "vendas"}
                </p>
              </div>
              <p className="mt-1.5 font-mono tabular-nums text-display-sm font-semibold text-emerald-700"
                data-testid="cash-kpi-total">{brl(summary?.sales_total ?? 0)}</p>

              <div className="mt-4 pt-4 border-t border-slate-100">
                <ByMethod byMethod={summary?.by_method} />
              </div>
            </Card>
          </div>

          {/* Extrato do turno: cada venda e cada movimentação da gaveta. */}
          <section className="space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="font-heading text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <ArrowLeftRight size={16} className="text-slate-400" aria-hidden /> Movimentações do caixa
                </h2>
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" size="sm" icon={ArrowDownCircle} onClick={() => setMovementType("suprimento")} data-testid="cash-suprimento-btn">
                    Suprimento
                  </Button>
                  {/* Só a sangria fica marcada em vermelho: é a que esvazia a gaveta. */}
                  <Button variant="dangerSoft" size="sm" icon={ArrowUpCircle} onClick={() => setMovementType("sangria")} data-testid="cash-sangria-btn">
                    Sangria
                  </Button>
                  {/* Ajuste fica em terceiro e neutro: é a exceção do turno, não
                      uma operação de rotina como as outras duas. */}
                  <Button variant="ghost" size="sm" icon={Scale} onClick={() => setAdjusting(true)} data-testid="cash-adjust-btn">
                    Ajuste
                  </Button>
                </div>
              </div>
              <DataTable
        rowTestId="cash-movement-row"
                caption="Movimentações do turno em aberto"
                rows={movements}
                // Sem rowKey de propósito: o extrato mistura venda e movimentação,
                // não há id único no payload — o DataTable cai no índice, como antes.
                density="compact"
                empty={{
                  icon: ArrowLeftRight,
                  title: "Nenhuma movimentação neste turno",
                  desc: "As vendas do PDV entram aqui sozinhas. Sangria e suprimento você lança por aqui.",
                  action: (
                    <Button variant="secondary" icon={ArrowDownCircle} onClick={() => setMovementType("suprimento")}>
                      Registrar suprimento
                    </Button>
                  ),
                  testId: "cash-movements-empty",
                }}
                columns={[
                  {
                    key: "hora", header: "Hora", mono: true, className: "text-slate-500", width: "5.5rem",
                    cell: (m) => hora(m.at),
                  },
                  {
                    key: "lancamento", header: "Lançamento", cardTitle: true,
                    cell: (m) => (
                      <>
                        <p className={`font-medium ${m.cancelled ? "text-slate-400 font-mono line-through" : m.kind === "venda" ? "text-slate-900 font-mono" : m.kind === "sangria" ? "text-red-600" : m.kind === "ajuste" ? "text-amber-700" : "text-emerald-700"}`}>{m.label}</p>
                        <p className="text-xs text-slate-400">{[m.description, m.seller_name, m.cancel_reason].filter(Boolean).join(" · ") || "—"}</p>
                        {/* Correção não apaga o erro: o valor de antes fica à
                            vista, que é o que o dono procura quando o caixa
                            fecha diferente três dias seguidos. */}
                        {m.edited_at && (
                          <p className="text-xs text-amber-600 mt-0.5" data-testid="cash-movement-edited">
                            Corrigido de {brl(Math.abs(m.original_amount))}
                            {m.edited_by_name ? ` por ${m.edited_by_name}` : ""}
                          </p>
                        )}
                      </>
                    ),
                  },
                  {
                    key: "pagamento", header: "Pagamento", className: "text-xs text-slate-600",
                    // A venda cancelada some do dinheiro, não do extrato: ela fica
                    // aqui com o valor original riscado, para o turno continuar
                    // fazendo sentido para quem confere a gaveta.
                    cell: (m) => (m.cancelled
                      ? <Badge tone="danger" dot>Cancelada · {brl(m.original_amount)}</Badge>
                      : m.payments?.length
                        ? m.payments.map((p, k) => (
                            <Badge key={k} className="mr-1 mb-0.5">
                              {METHOD_LABEL[p.method] || p.method}{p.installments > 1 ? ` ${p.installments}x` : ""} · {brl(p.amount)}
                            </Badge>
                          ))
                        : <span className="text-slate-400">Dinheiro (gaveta)</span>),
                  },
                  {
                    key: "valor", header: "Valor", align: "right", mono: true, className: "font-semibold",
                    cell: (m) => (
                      <span className={m.cancelled ? "text-slate-300" : m.amount < 0 ? "text-red-600" : "text-slate-900"}>
                        {m.amount < 0 ? "− " : ""}{brl(Math.abs(m.amount))}
                      </span>
                    ),
                  },
                  {
                    key: "acoes", header: "", align: "right", width: "7rem",
                    // Só movimentação da gaveta. Venda não se edita por aqui: ela
                    // se desfaz cancelando, em Vendas do balcão, com estorno de
                    // estoque — deixar "editar" ao lado dela convidaria ao errado.
                    cell: (m) => (m.kind === "venda" ? null : (
                      <div className="flex justify-end gap-0.5">
                        {/* Sangria e suprimento saem em papel na hora — a
                            sangria vai grampeada no envelope do depósito. O
                            ajuste não: ele é acerto de conferência, não
                            entrega de dinheiro a ninguém. */}
                        {m.kind !== "ajuste" && (
                          <Button variant="ghost" size="iconSm" aria-label={`Imprimir comprovante de ${m.label}`}
                            onClick={() => imprimirMovimento(m)} data-testid="cash-movement-print"><Printer size={14} /></Button>
                        )}
                        <Button variant="ghost" size="iconSm" aria-label={`Corrigir ${m.label}`}
                          onClick={() => setEditingMovement(m)} data-testid="cash-movement-edit"><Pencil size={14} /></Button>
                        <Button variant="ghost" size="iconSm" aria-label={`Remover ${m.label}`}
                          onClick={() => setRemovingMovement(m)} className="hover:text-red-600"
                          data-testid="cash-movement-remove"><Trash2 size={14} /></Button>
                      </div>
                    )),
                  },
                ]}
              />
            </section>

        </div>
      )}

      {/* Antes do histórico e depois do turno: é a leitura de fim de
          expediente, e vale tanto com o caixa aberto quanto fechado. */}
      <DaySummary data={daySummary} date={day} onDateChange={setDay} loading={dayLoading} />

      <section className="space-y-3">
        <h2 className="font-heading text-sm font-semibold text-slate-900 flex items-center gap-2">
          <History size={16} className="text-slate-400" aria-hidden /> Histórico de caixas
        </h2>
        <DataTable
        rowTestId="cash-history-row"
          caption="Turnos de caixa já registrados"
          rows={history.items}
          rowKey={(s) => s.id}
          loading={history.isLoading}
          error={history.isError}
          onRetry={history.refetch}
          empty={{
            icon: History,
            title: "Nenhum caixa no histórico",
            desc: "Cada turno entra aqui no fechamento, com o esperado, o contado e a diferença.",
            action: session
              ? <Button variant="secondary" icon={Lock} onClick={() => setClosing(true)}>Fechar o caixa atual</Button>
              : <Button icon={LockOpen} onClick={() => openAmountRef.current?.focus()}>Abrir o primeiro caixa</Button>,
            testId: "cash-history-empty",
          }}
          columns={[
            {
              key: "abertura", header: "Abertura", className: "text-slate-500",
              cell: (s) => new Date(s.opened_at).toLocaleString("pt-BR"),
            },
            { key: "fechamento", header: "Fechamento", className: "text-slate-500", cell: (s) => (s.closed_at ? new Date(s.closed_at).toLocaleString("pt-BR") : "—") },
            { key: "operador", header: "Operador", className: "text-slate-700", cell: (s) => s.opened_by_name },
            { key: "vendas", header: "Vendas", align: "right", mono: true, cell: (s) => brl(s.summary?.sales_total ?? 0) },
            { key: "esperado", header: "Esperado", align: "right", mono: true, cell: (s) => brl(s.expected_amount ?? 0) },
            { key: "contado", header: "Contado", align: "right", mono: true, cell: (s) => brl(s.closing_amount ?? 0) },
            {
              key: "diferenca", header: "Diferença", align: "right", mono: true,
              cell: (s) => <Badge tone={diffTone(s.difference ?? 0)}>{brl(s.difference ?? 0)}</Badge>,
            },
          ]}
        />
        <Pagination page={history.page} lastPage={history.lastPage} total={history.total}
          perPage={20} onChange={history.setPage} label="sessões" />
      </section>

      {movementType && (
        <MovementDialog
          type={movementType}
          onClose={() => setMovementType(null)}
          onDone={() => { setMovementType(null); load(); }}
        />
      )}
      {editingMovement && (
        <MovementDialog
          movement={editingMovement}
          onClose={() => setEditingMovement(null)}
          onDone={() => { setEditingMovement(null); load(); }}
        />
      )}
      {adjusting && (
        <AdjustDialog
          expected={expectedCash}
          onClose={() => setAdjusting(false)}
          onDone={() => { setAdjusting(false); load(); }}
        />
      )}
      {/* O esperado por forma vem do servidor (`expected_by_method`): a conta do
          dinheiro embute fundo de troco, sangria, suprimento e ajuste, e
          reproduzi-la aqui era como as duas pontas passariam a discordar. */}
      {closing && current?.expected_by_method && (
        <CloseCashDialog
          expectedByMethod={current.expected_by_method}
          onClose={() => setClosing(false)}
          onDone={() => { setClosing(false); load(); }}
        />
      )}
      <ConfirmDialog
        open={!!removingMovement}
        onClose={() => setRemovingMovement(null)}
        onConfirm={() => removeMovement(removingMovement)}
        title="Remover lançamento"
        message={removingMovement
          ? `Remover ${removingMovement.label.toLowerCase()} de ${brl(Math.abs(removingMovement.amount))}? O esperado em caixa é recalculado na hora.`
          : ""}
        confirmLabel="Remover"
      />
    </div>
  );
}
